import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useContext, useState} from 'react';
import {Dropdown} from 'semantic-ui-react';

import {Analytics} from '../../services/analytics';
import * as S from './CopyLinkSection.styles';
import {StyledDropdown} from './Dropdown.styles';
import {ShareReportTriggerContext} from './ShareReportTriggerContextProvider';
import {
  useMaybeSurreptitiouslyCreatePreviewFriendlyURL,
  type UseMaybeSurreptitiouslyCreatePreviewFriendlyURLInput,
} from './util';

const DROPDOWN_TEXT = [
  ['Anyone with a link can ', 'view', 'Anyone with a link can view'],
  [
    'Only those who are ',
    'invited have access',
    'Only those who are invited have access',
  ],
  'Team members with a link can view',
];

export const CopyLinkSection = ({
  currentPreviewFriendlyURL,
  magicLinkURL,
  normalLinkURL,
  handleToggleMagicLink,
  isSharingLinkEnabled,
  useMaybeSurreptitiouslyCreatePreviewFriendlyURLProps,
  selectedNavTab,
  setShouldDeleteView,
}: {
  currentPreviewFriendlyURL: string | undefined;
  normalLinkURL: string;
  magicLinkURL: string;
  isSharingLinkEnabled: boolean;
  handleToggleMagicLink: () => void;
  useMaybeSurreptitiouslyCreatePreviewFriendlyURLProps: UseMaybeSurreptitiouslyCreatePreviewFriendlyURLInput;
  selectedNavTab: string;
  setShouldDeleteView: (b: boolean) => void;
}) => {
  const {disableMagicLinkSharing, view, isPrivateAccess} = useContext(
    ShareReportTriggerContext
  );
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const isPrivateSharingMagicLinkAllowed =
    isPrivateAccess && !disableMagicLinkSharing;
  useMaybeSurreptitiouslyCreatePreviewFriendlyURL(
    useMaybeSurreptitiouslyCreatePreviewFriendlyURLProps
  );

  const handleCopyLink = () => {
    if (isCopied) {
      return;
    }
    const copyText = isPrivateSharingMagicLinkAllowed
      ? isSharingLinkEnabled
        ? // if private and sharing is enabled, we want the pfurl or the magic link
          currentPreviewFriendlyURL || magicLinkURL
        : // if private and sharing disabled, we dont need a pfurl, since its between team members only
          normalLinkURL
      : currentPreviewFriendlyURL || normalLinkURL;
    copyToClipboard(copyText);
    toast('Link copied');
    setShouldDeleteView(false);
    Analytics.track('Report View Only Link Copied', {
      reportId: view.cid,
      isPreviewFriendlyLink:
        currentPreviewFriendlyURL != null &&
        !(isPrivateSharingMagicLinkAllowed && !isSharingLinkEnabled),
      isMagicLink: isPrivateSharingMagicLinkAllowed && isSharingLinkEnabled,
      currentTab: selectedNavTab,
    });
    // Animates text to Copied! for 3 secs
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  };

  const toggleSharing = (shouldToggleOn: boolean) => {
    // toggles sharing if shouldToggleOn is not what is curently set
    if (shouldToggleOn !== isSharingLinkEnabled) {
      handleToggleMagicLink();
    }
  };

  return (
    <S.RowWrapper>
      <Button variant="ghost" onClick={handleCopyLink}>
        <S.CopyLink $isCopied={isCopied}>
          <Icon name="link-alt" />
          {isCopied ? 'Copied!' : 'Copy report link'}
        </S.CopyLink>
      </Button>
      {isPrivateSharingMagicLinkAllowed ? (
        <PermissionDropdown
          isSharingLinkEnabled={isSharingLinkEnabled}
          toggleSharing={toggleSharing}
        />
      ) : (
        <S.LabelText>
          {isPrivateAccess ? DROPDOWN_TEXT[2] : DROPDOWN_TEXT[0][2]}
        </S.LabelText>
      )}
    </S.RowWrapper>
  );
};
const PermissionDropdown = ({
  isSharingLinkEnabled,
  toggleSharing,
}: {
  isSharingLinkEnabled: boolean;
  toggleSharing: (shouldToggleOn: boolean) => void;
}) => (
  <StyledDropdown
    direction="left"
    trigger={
      <S.DropdownTriggerWrapper>
        <S.DropdownTextWrapper>
          <S.DropdownLabel>
            {DROPDOWN_TEXT[isSharingLinkEnabled ? 0 : 1][0]}
          </S.DropdownLabel>
          <S.DropdownBold>
            {DROPDOWN_TEXT[isSharingLinkEnabled ? 0 : 1][1]}{' '}
          </S.DropdownBold>
        </S.DropdownTextWrapper>
        <S.GrayIcon name="chevron-down" />
      </S.DropdownTriggerWrapper>
    }
    // Removes the default semantic dropdown icon
    icon={<></>}>
    <Dropdown.Menu>
      <Dropdown.Item
        selected={isSharingLinkEnabled}
        onClick={() => toggleSharing(true)}>
        {DROPDOWN_TEXT[0][2]} {isSharingLinkEnabled && <S.CheckmarkIcon />}
      </Dropdown.Item>
      <Dropdown.Item
        selected={!isSharingLinkEnabled}
        onClick={() => toggleSharing(false)}>
        {DROPDOWN_TEXT[1][2]}
        {!isSharingLinkEnabled && <S.CheckmarkIcon />}
      </Dropdown.Item>
    </Dropdown.Menu>
  </StyledDropdown>
);
